import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { showRewards } from "@/lib/rewards";
import Icon from "@/components/Icon";
import { catColor } from "@/lib/colors";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Challenges() {
  const { refreshUser } = useAuth();
  const [challenges, setChallenges] = useState(null);
  const [active, setActive] = useState(null);
  const [proofText, setProofText] = useState("");
  const [proofImg, setProofImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/challenges").then((r) => setChallenges(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setProofImg(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/challenges/submit", { challenge_id: active.key, proof_text: proofText, proof_image: proofImg });
      showRewards(data);
      refreshUser();
      setActive(null); setProofText(""); setProofImg(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (!challenges) return <div className="grid place-items-center py-32"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;

  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-1">Real-World Challenges</h1>
      <p className="text-muted-foreground mb-6">Take your skills off the screen. Complete real tasks, upload proof, earn big rewards.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        {challenges.map((c, i) => {
          const col = catColor(catColorKey(c.category));
          return (
            <motion.div key={c.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className={`rounded-3xl glass border p-5 ${c.completed ? "border-emerald-500/30" : col.border}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${col.grad} grid place-items-center shadow-lg shrink-0`}><Icon name={c.icon} size={22} className="text-white" /></div>
                <div className="flex-1">
                  <h2 className="font-display font-bold text-base">{c.title}</h2>
                  <p className="text-muted-foreground text-sm mt-0.5">{c.desc}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <span className="text-xs font-bold text-yellow-400">+{c.xp} XP</span>
                  <span className="text-xs font-bold text-emerald-400">+{c.coins} coins</span>
                </div>
                {c.completed ? (
                  <span className="flex items-center gap-1.5 text-sm font-bold text-emerald-400"><CheckCircle2 size={16} /> Done</span>
                ) : (
                  <Button data-testid={`challenge-start-${c.key}`} onClick={() => setActive(c)} className="rounded-full h-9 px-4 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold">Complete</Button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="glass border-white/10 rounded-3xl">
          <DialogHeader><DialogTitle className="font-display">{active?.title}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Complete this real-world challenge and submit your proof to earn rewards.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{active?.desc}</p>
          <Textarea data-testid="proof-text" value={proofText} onChange={(e) => setProofText(e.target.value)} placeholder="Describe how you completed it…" className="rounded-2xl bg-white/5 border-white/10 min-h-24" />
          {active?.proof && (
            <div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
              {proofImg ? (
                <div className="relative"><img src={proofImg} alt="proof" className="rounded-2xl max-h-48 w-full object-cover" /><button onClick={() => setProofImg(null)} className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60"><X size={16} /></button></div>
              ) : (
                <button data-testid="upload-proof-btn" onClick={() => fileRef.current?.click()} className="w-full rounded-2xl border border-dashed border-white/20 py-6 grid place-items-center text-muted-foreground hover:border-indigo-500/40 transition-colors"><Upload size={22} className="mb-1" /><span className="text-xs font-semibold">Upload proof photo (optional)</span></button>
              )}
            </div>
          )}
          <Button data-testid="submit-challenge-btn" onClick={submit} disabled={busy} className="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">{busy ? <Loader2 className="animate-spin" size={18} /> : "Submit & Claim Rewards"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function catColorKey(cat) {
  return { money: "emerald", career: "cyan", health: "rose", law: "amber" }[cat] || "indigo";
}
